/**
 * Safe Storage Cleanup Utility
 *
 * Removes legacy IndexedDB databases and localStorage keys that were replaced
 * by the unified TotalAccessOfflineDB. This is safe to run in production as it
 * only targets known legacy storage locations and preserves all critical keys.
 *
 * Legacy databases to remove:
 * - TotalAccessOfflineAuthDB (migrated to offlineDb offline-auth domain)
 * - totalaccess_email_local_cache (migrated to offlineDb emails domain)
 * - TotalAccessEmailOfflineDB (migrated to offlineDb emails domain)
 * - TotalAccessMessagingOfflineDB (migrated to offlineDb messages domain)
 * - TotalAccessEncryptedStorage (migrated to offlineDb per-domain stores)
 * - totalaccess_forms_offline (migrated to offlineDb workitems domain)
 *
 * Legacy localStorage keys to remove:
 * - emailPreferences, emailTheme (migrated to offlineDb emails meta)
 * - totalaccess_forms_offline_* prefixes (migrated to offlineDb)
 * - Other deprecated keys
 */

// Critical localStorage keys that must NEVER be removed
const CRITICAL_LOCAL_STORAGE_KEYS = new Set([
  'auth_tokens',
  'user_data',
  'current_client_id',
  'offline_navigation_data', // Still actively used by navigation service
  'totalaccess_active_industry_bundle',
  'smartclock_default_landing',
  'user_is_field_worker',
  'dashboard_cached_stats',
  'smart_task_navigation_override',
  'smarttask_view_mode',
  'smarttask_filters',
  'smart_task_display_mode',
  'displayMode',
]);

// Legacy IndexedDB database names to remove
const LEGACY_INDEXED_DB_NAMES = [
  'TotalAccessOfflineAuthDB',
  'totalaccess_email_local_cache',
  'TotalAccessEmailOfflineDB',
  'TotalAccessMessagingOfflineDB',
  'TotalAccessEncryptedStorage',
  'totalaccess_forms_offline',
] as const;

// Legacy localStorage key patterns to remove
const LEGACY_LOCAL_STORAGE_PATTERNS = [
  /^totalaccess_forms_offline_/,
  /^offline_user_data$/,
  /^offline_permission_data$/,
  /^offline_client_data$/,
  /^offline_user_data$/,
  /^offline_permissions$/,
  /^offline_last_sync$/,
  /^cached_employees$/,
  /^emailPreferences$/,
  /^emailTheme$/,
  /^lastVisitedModule$/,
  /^support_diagnostic_pending_ref$/,
] as const;

// Migration guard keys - if these exist, migration already happened
const MIGRATION_GUARD_KEYS = [
  '__ta_offline_auth_migrated_to_unified_db_v1__',
  '__ta_email_ui_cache_migrated_to_unified_db_v2__',
  'legacy_operational_forms_storage_purged_v1',
] as const;

interface CleanupResult {
  success: boolean;
  indexedDbDeleted: string[];
  localStorageRemoved: string[];
  localStoragePreserved: string[];
  errors: string[];
  bytesFreed: number;
}

/**
 * Estimates localStorage usage in bytes
 */
function estimateLocalStorageUsage(): number {
  if (typeof window === 'undefined') return 0;
  
  let total = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key) {
      total += key.length + (localStorage.getItem(key)?.length || 0);
    }
  }
  return total;
}

/**
 * Detects whether an IndexedDB database with the given name already exists.
 * Uses indexedDB.databases() when available, otherwise opens the database and
 * aborts creation if it does not already exist.
 */
async function indexedDbExists(name: string): Promise<boolean> {
  if (typeof indexedDB === 'undefined') {
    return false;
  }

  const indexedDbWithDatabases = indexedDB as IDBFactory & {
    databases?: () => Promise<Array<{ name?: string }>>;
  };

  if (typeof indexedDbWithDatabases.databases === 'function') {
    try {
      const databases = await indexedDbWithDatabases.databases();
      return databases.some((db) => db.name === name);
    } catch {
      // Fall back to legacy detection if databases() is unavailable.
    }
  }

  return new Promise((resolve) => {
    let created = false;
    const request = indexedDB.open(name);

    request.onupgradeneeded = () => {
      created = true;
      request.transaction?.abort();
    };

    request.onsuccess = () => {
      const db = request.result;
      db.close();
      if (created) {
        indexedDB.deleteDatabase(name);
        resolve(false);
      } else {
        resolve(true);
      }
    };

    request.onerror = () => {
      resolve(false);
    };

    request.onblocked = () => {
      // Non-critical: just means another tab/worker has an open connection.
      // We treat it as "doesn't exist" so cleanup proceeds and deletion is
      // attempted later.
      resolve(false);
    };
  });
}

/**
 * Deletes an IndexedDB database. Retries once after a short delay if blocked
 * (which happens when a stale connection hasn't released its lock yet).
 */
async function deleteIndexedDB(name: string, retryDelayMs = 1500): Promise<boolean> {
  const attemptDelete = (): Promise<boolean> =>
    new Promise((resolve) => {
      const request = indexedDB.deleteDatabase(name);

      request.onsuccess = () => resolve(true);
      request.onerror = () => resolve(false);
      request.onblocked = () => resolve(false);
    });

  const result = await attemptDelete();
  if (result) return true;

  // If the first attempt failed (likely blocked), wait and retry once.
  // Stale Dexie connections often release their lock within a few hundred
  // milliseconds of the page loading.
  await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
  return attemptDelete();
}

/**
 * Removes legacy localStorage keys that match patterns
 */
function removeLegacyLocalStorageKeys(): { removed: string[]; preserved: string[] } {
  if (typeof window === 'undefined') {
    return { removed: [], preserved: [] };
  }

  const removed: string[] = [];
  const preserved: string[] = [];

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    // Skip critical keys
    if (CRITICAL_LOCAL_STORAGE_KEYS.has(key)) {
      preserved.push(key);
      continue;
    }

    // Skip migration guard keys
    if (MIGRATION_GUARD_KEYS.includes(key as typeof MIGRATION_GUARD_KEYS[number])) {
      preserved.push(key);
      continue;
    }

    // Check if key matches any legacy pattern
    const isLegacy = LEGACY_LOCAL_STORAGE_PATTERNS.some(pattern => pattern.test(key));
    
    if (isLegacy) {
      try {
        localStorage.removeItem(key);
        removed.push(key);
      } catch (err) {
        console.warn(`[storageCleanup] Failed to remove localStorage key ${key}:`, err);
        preserved.push(key);
      }
    } else {
      preserved.push(key);
    }
  }

  return { removed, preserved };
}

/**
 * Main cleanup function - safely removes all legacy storage
 */
export async function cleanupLegacyStorage(): Promise<CleanupResult> {
  const result: CleanupResult = {
    success: false,
    indexedDbDeleted: [],
    localStorageRemoved: [],
    localStoragePreserved: [],
    errors: [],
    bytesFreed: 0,
  };

  if (typeof window === 'undefined') {
    result.errors.push('Not running in browser environment');
    return result;
  }

  // Estimate storage before cleanup
  const usageBefore = estimateLocalStorageUsage();

  try {
    // 1. Delete legacy IndexedDB databases
    for (const dbName of LEGACY_INDEXED_DB_NAMES) {
      try {
        const deleted = await deleteIndexedDB(dbName);
        if (deleted) {
          result.indexedDbDeleted.push(dbName);
          console.log(`[storageCleanup] Deleted legacy IndexedDB: ${dbName}`);
        } else {
          console.log(`[storageCleanup] Legacy IndexedDB not found or could not delete: ${dbName}`);
        }
      } catch (err) {
        const msg = `Failed to delete IndexedDB ${dbName}: ${err instanceof Error ? err.message : String(err)}`;
        result.errors.push(msg);
        console.warn(msg);
      }
    }

    // 2. Remove legacy localStorage keys
    const lsResult = removeLegacyLocalStorageKeys();
    result.localStorageRemoved = lsResult.removed;
    result.localStoragePreserved = lsResult.preserved;

    if (lsResult.removed.length > 0) {
      console.log(`[storageCleanup] Removed ${lsResult.removed.length} legacy localStorage keys`);
    }

    // Calculate bytes freed (localStorage only - IndexedDB is harder to estimate)
    const usageAfter = estimateLocalStorageUsage();
    result.bytesFreed = Math.max(0, usageBefore - usageAfter);

    result.success = true;
  } catch (err) {
    result.errors.push(`Cleanup failed: ${err instanceof Error ? err.message : String(err)}`);
    console.error('[storageCleanup] Cleanup failed:', err);
  }

  return result;
}

/**
 * Check if legacy storage exists without performing cleanup
 */
export async function hasLegacyStorage(): Promise<{
  hasLegacyIndexedDB: string[];
  hasLegacyLocalStorage: string[];
}> {
  const result = {
    hasLegacyIndexedDB: [] as string[],
    hasLegacyLocalStorage: [] as string[],
  };

  if (typeof window === 'undefined') {
    return result;
  }

  // Check for legacy IndexedDB databases
  for (const dbName of LEGACY_INDEXED_DB_NAMES) {
    try {
      const exists = await indexedDbExists(dbName);
      if (exists) {
        result.hasLegacyIndexedDB.push(dbName);
      }
    } catch {
      // Assume doesn't exist on error
    }
  }

  // Check for legacy localStorage keys
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;

    if (CRITICAL_LOCAL_STORAGE_KEYS.has(key)) continue;
    if (MIGRATION_GUARD_KEYS.includes(key as typeof MIGRATION_GUARD_KEYS[number])) continue;

    const isLegacy = LEGACY_LOCAL_STORAGE_PATTERNS.some(pattern => pattern.test(key));
    if (isLegacy) {
      result.hasLegacyLocalStorage.push(key);
    }
  }

  return result;
}

/**
 * Run cleanup automatically if legacy storage is detected
 * This is safe to call on app initialization
 */
export async function autoCleanupLegacyStorage(): Promise<void> {
  try {
    const legacy = await hasLegacyStorage();
    const hasLegacy = legacy.hasLegacyIndexedDB.length > 0 || legacy.hasLegacyLocalStorage.length > 0;

    if (hasLegacy) {
      console.warn('[storageCleanup] Legacy storage detected:', legacy);
      console.log('[storageCleanup] Running automatic cleanup...');
    } else {
      console.log('[storageCleanup] No legacy storage detected; forcing cleanup pass to remove stale IndexedDB names if present');
    }

    const result = await cleanupLegacyStorage();

    if (result.success) {
      console.log('[storageCleanup] Cleanup completed successfully:', {
        indexedDbDeleted: result.indexedDbDeleted.length,
        localStorageRemoved: result.localStorageRemoved.length,
        bytesFreed: result.bytesFreed,
      });
    } else {
      console.error('[storageCleanup] Cleanup failed:', result.errors);
    }
  } catch (err) {
    console.error('[storageCleanup] Auto-cleanup failed:', err);
  }
}
